import { FC, useCallback, useContext, useState } from 'react'

import {
  Alert,
  Button,
  Dialog,
  Flex,
  HStack,
  Menu,
  Portal,
  Spacer,
  Spinner,
  Square,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { DownloadIcon, SettingIcon } from '@editor/assets/icons'
import { MODAL_TYPES } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { InspectionArea } from '@editor/interfaces/interfaces'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { downloadCadFile } from '@editor/services/AWS'
import { getSignedUrlForCadFile, postBlueprintCadFiles } from '@editor/services/InspectionArea'
import { withPromiseContained } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { Tooltip } from '@infraloop/common'
import fileDownload from 'js-file-download'

const BlueprintCADExportButton: FC = () => {
  const { open, onClose, onOpen } = useDisclosure()

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const selectedBlueprint = useAppSelector((state) => state.blueprint.editor.selectedBlueprint)
  const drawings = useAppSelector((state) => state.blueprint.drawings.document.drawings)

  // Context
  const { showModal, showErrorModal } = useContext(GlobalModalContext)
  const { getAccessToken } = useContext(UserContext)

  // State for download progress
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState(0)

  // Check if export is possible
  const hasInspectionItems = Boolean(selectedBlueprint?.inspection_items?.length || selectedBlueprint?.polygons?.length)
  const hasDrawings = Boolean(drawings.length)

  const hasContent = hasDrawings || hasInspectionItems

  const canExport = Boolean(
    project?.project_id && inspectionArea?.inspection_area_id && selectedBlueprint?.sort_key && hasContent,
  )
  const errorMessage = (() => {
    if (canExport) return null
    if (!hasContent) return '損傷、境界の要素がありません'
    return 'ブループリントが選択されていません'
  })()

  /**
   * Reset the modal state.
   */
  const reset = useCallback(() => {
    setProgress(0)
    if (!isExporting) {
      dispatch(setAttentionText({ message: '' }))
    }
  }, [dispatch, isExporting])

  /**
   * Download blueprint CAD file
   */
  const download = () => {
    if (!project?.project_id || !inspectionArea?.inspection_area_id || !selectedBlueprint?.blueprint_id) {
      return
    }

    dispatch(
      setAttentionText({
        message: 'ブループリントCADファイルを生成中です。しばらくお待ちください。',
      }),
    )

    void withPromiseContained(async () => {
      const token = await getAccessToken()
      if (!token) return

      setIsExporting(true)

      // Generate blueprint CAD file
      const generatedCad: InspectionArea | null = await postBlueprintCadFiles(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        selectedBlueprint.blueprint_id,
        showErrorModal,
      )

      // If fails, reset and return
      if (!generatedCad) {
        return
      }

      // Extract the blueprint CAD file name with proper type safety
      const generatedFileName = generatedCad.cad_file_blueprint?.name

      if (!generatedFileName) {
        showModal({
          body: 'データが存在しません。',
          modalType: MODAL_TYPES.ERROR,
        })
        return
      }

      // Get signed URL for blueprint CAD file
      const downloadUrl = await getSignedUrlForCadFile(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        showErrorModal,
        'blueprint',
      )
      if (!downloadUrl) return

      // Download CAD file from signed URL
      const downloadResult = await downloadCadFile(downloadUrl, setProgress, showErrorModal)
      if (!downloadResult) return

      // File download
      const { blob: blobParts, type } = downloadResult
      const fileBlob = new Blob(blobParts, { type })
      fileDownload(fileBlob, generatedFileName, type)
    }).finally(() => {
      setIsExporting(false)
      reset()
    })
  }

  return (
    <>
      <Tooltip
        showArrow
        label={!canExport ? errorMessage : ''}
        disabled={canExport}
        positioning={{ placement: 'left' }}
      >
        <Menu.Item
          value="blueprint-cad-export"
          disabled={!canExport}
          opacity={!canExport ? 0.6 : 1}
          cursor={!canExport ? 'not-allowed' : 'pointer'}
          onClick={canExport ? onOpen : undefined}
        >
          <Flex direction="column" w="100%" data-testid="blueprint-cad-export-button">
            <Flex direction="row">
              <Square fontSize={{ base: 'xl', xl: 'lg' }} className="chakra-menu__icon-wrapper">
                <DownloadIcon />
              </Square>
              <Text>CAD出力</Text>
            </Flex>
            {isExporting ? (
              <Flex fontSize="0.8rem" color="whiteAlpha.600">
                <Square className="chakra-menu__icon-wrapper">
                  <Spinner size="xs" />
                </Square>
                <Text fontSize="xs">ブループリントCAD生成中..</Text>
              </Flex>
            ) : null}
          </Flex>
        </Menu.Item>
      </Tooltip>

      <Dialog.Root
        closeOnInteractOutside
        open={open}
        onOpenChange={(e) => {
          if (!e.open) onClose()
        }}
        trapFocus
        placement="center"
        size="lg"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="md">
                <HStack>
                  <SettingIcon size="1.5rem" />
                  <Text>ブループリントCAD出力</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body position="relative">
                <VStack gap={4} align="stretch">
                  <Alert.Root status="warning" borderRadius="md">
                    <Alert.Indicator />
                    <Alert.Content>
                      <VStack align="stretch" gap={2} w="100%">
                        <Text fontSize="sm" fontWeight="medium">
                          ⚠️ 注意事項
                        </Text>
                        <Text fontSize="sm">
                          この機能は現在開発中です。出力されるCADファイルは、作成したブループリントデザインと完全に同一でない場合があります。
                        </Text>
                      </VStack>
                    </Alert.Content>
                  </Alert.Root>

                  <Text>選択されたブループリントからCADファイルを生成します。</Text>

                  {selectedBlueprint && (
                    <Text color="gray2.600" fontSize="sm">
                      ブループリント: {selectedBlueprint.name || 'Unnamed Blueprint'}
                    </Text>
                  )}

                  {isExporting && (
                    <Alert.Root status="info" mt={2} mb={2} px={2} py={1} borderRadius={5}>
                      <Alert.Indicator asChild mr={2} boxSize={4}>
                        <Spinner />
                      </Alert.Indicator>
                      <Alert.Title>ブループリントCADファイル生成中.. {progress}%</Alert.Title>
                    </Alert.Root>
                  )}

                  {errorMessage && (
                    <Text color="red.500" fontSize="sm">
                      {errorMessage}
                    </Text>
                  )}
                </VStack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button variant="outline" onClick={onClose}>
                  キャンセル
                </Button>
                <Spacer />
                <Button disabled={!canExport || isExporting} colorPalette="blue" onClick={download}>
                  ファイル生成
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}

export default BlueprintCADExportButton
